/* eslint-env browser */
// Reports › Scout onboarding report (wave 3, W2_Portal_Tables look): every
// fleet admin, call-centre agent / supervisor and scout with the number of
// drivers they onboarded, most first; "Show details" under a row lists those
// drivers (car, licence pictures, online, registered).
//
// Data, exactly as before (no new reads): the user list (fetchUsersOnce)
// and the cars list, fetched once per mount only when this session has no
// fresh full list (carsFresh / loadCars). Fleet admins, call-centre agents
// and scouts see only their own drivers; admin, supervisors and finance see
// everyone. Phone and email are hidden in demo mode; the CSV (critical edits
// on) has the same columns as before.
// Call-centre feedback (29 Oct 2026): a copy button beside each number copies
// it without spaces (+251964540448) for MicroSIP; it is not there while
// numbers are hidden.
import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { api } from 'common';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import { tokens as c } from '../theme';
import { formatAddisDateTime } from '../common/exportUtils';
import { CopyButton, DataTable, YesNo, calmCaps, EMPTY_TEXT } from '../components/modern';
import { carsFresh, loadCars } from '../utils/carsSession';

const NUMBER_FORMAT = new Intl.NumberFormat('en-US');
const fmt = (n) => NUMBER_FORMAT.format(n || 0);

export default function FleetDriversReport() {
  const { t, i18n } = useTranslation();
  const settings = useSelector((state) => state.settingsdata.settings);
  const { fetchUsersOnce, fetchCars } = api;
  const [data, setData] = useState([]);
  const staticusers = useSelector((state) => state.usersdata.staticusers);
  const usersLoading = useSelector((state) => state.usersdata.loading);
  const usersError = useSelector((state) => state.usersdata.error);
  const carlistdata = useSelector((state) => state.carlistdata);
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchUsersOnce());
  }, [dispatch, fetchUsersOnce]);

  // Cars are no longer pre-fetched at login for staff roles. Fetch ONCE per
  // mount, and only when this session has no fresh full list (5.8 MB read).
  // Guard evaluated outside the effect + empty deps on purpose: FETCH_CARS
  // replaces the slice object before the data lands, so an effect depending on
  // the slice re-dispatches on every render.
  const carsFreshAtMount = carsFresh(carlistdata, auth.profile && auth.profile.uid);
  useEffect(() => {
    if (!carsFreshAtMount) loadCars(dispatch, fetchCars, auth.profile.uid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (staticusers && carlistdata.cars) {
      // Get all fleet admins and call center users who can register drivers
      const fleetAdmins = staticusers.filter(
        (user) => user.usertype === 'fleetadmin' || user.usertype === 'callcenteragent' || user.usertype === 'scout' || user.usertype === 'callcentersupervisor'
      );

      // Get all drivers
      const drivers = staticusers.filter((user) => user.usertype === 'driver');

      // Create a map of driver cars
      const driverCarsMap = {};
      if (carlistdata.cars) {
        carlistdata.cars.forEach((car) => {
          if (car.driver) {
            if (!driverCarsMap[car.driver]) {
              driverCarsMap[car.driver] = [];
            }
            driverCarsMap[car.driver].push(car);
          }
        });
      }

      // Create a map to count drivers per fleet admin
      const fleetDriverMap = {};

      // Initialize all fleet admins with 0 drivers
      fleetAdmins.forEach((admin) => {
        fleetDriverMap[admin.id] = {
          fleetAdminId: admin.id,
          fleetAdminName: `${admin.firstName} ${admin.lastName}`,
          fleetAdminEmail: settings.AllowCriticalEditsAdmin ? admin.email : t('hidden_demo'),
          fleetAdminMobile: settings.AllowCriticalEditsAdmin ? admin.mobile : t('hidden_demo'),
          profile_image: admin.profile_image,
          userType: admin.usertype,
          driverCount: 0,
          drivers: [],
        };
      });

      // Count drivers for each fleet admin
      drivers.forEach((driver) => {
        if (driver.fleetadmin && fleetDriverMap[driver.fleetadmin]) {
          const hasCar = driverCarsMap[driver.id] && driverCarsMap[driver.id].length > 0;
          const hasLicenseFront = !!driver.licenseImage;
          const hasLicenseBack = !!driver.licenseImageBack;
          const isApproved = !!driver.approved;

          fleetDriverMap[driver.fleetadmin].driverCount++;
          fleetDriverMap[driver.fleetadmin].drivers.push({
            id: driver.id,
            name: `${driver.firstName} ${driver.lastName}`,
            mobile: settings.AllowCriticalEditsAdmin ? driver.mobile : t('hidden_demo'),
            email: settings.AllowCriticalEditsAdmin ? driver.email : t('hidden_demo'),
            hasCar: hasCar,
            carCount: driverCarsMap[driver.id] ? driverCarsMap[driver.id].length : 0,
            hasLicenseFront: hasLicenseFront,
            hasLicenseBack: hasLicenseBack,
            isApproved: isApproved,
            driverActiveStatus: driver.driverActiveStatus || false,
            createdAt: driver.createdAt || null,
          });
        }
      });

      // Convert to array
      const reportData = Object.values(fleetDriverMap);

      // Filter based on user role
      let filteredData = reportData;
      if (auth.profile.usertype === 'fleetadmin' || auth.profile.usertype === 'callcenteragent' || auth.profile.usertype === 'scout') {
        // Fleet admins, call center agents, and scouts only see their own drivers
        filteredData = reportData.filter((item) => item.fleetAdminId === auth.profile.uid);
      }
      // Admin, call center supervisors, and finance see all data (including all scouts)

      setData(filteredData.sort((a, b) => b.driverCount - a.driverCount));
    } else {
      setData([]);
    }
  }, [staticusers, carlistdata.cars, settings.AllowCriticalEditsAdmin, auth.profile.usertype, auth.profile.uid, t]);

  const typeMap = useMemo(
    () => ({
      fleetadmin: t('fleetadmin', { defaultValue: 'Oromia Bank Branch' }),
      callcenteragent: t('call_center_agent', { defaultValue: 'Call Center Agent' }),
      callcentersupervisor: t('call_center_supervisor', { defaultValue: 'Call Center Supervisor' }),
      scout: t('scout', { defaultValue: 'Scout' }),
    }),
    [t]
  );

  // A number with its copy button (none while numbers are hidden: the cell
  // then holds the "hidden" words, not a number).
  const phoneWord = t('phone', { defaultValue: 'Phone number' });
  const phoneWithCopy = (mobile) =>
    settings.AllowCriticalEditsAdmin && mobile ? (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
        {mobile}
        <CopyButton value={mobile} phone what={phoneWord} sx={{ my: -0.5 }} />
      </Box>
    ) : (
      mobile
    );

  const columns = [
    { id: 'name', title: t('name', { defaultValue: 'Name' }), field: 'fleetAdminName', render: (row) => <Box component="span" sx={{ fontWeight: 600 }}>{row.fleetAdminName}</Box> },
    {
      id: 'userType',
      title: t('user_type', { defaultValue: 'User Type' }),
      field: 'userType',
      searchValue: (row) => typeMap[row.userType] || row.userType || '',
      sortValue: (row) => typeMap[row.userType] || row.userType || '',
      render: (row) => typeMap[row.userType] || row.userType,
    },
    {
      id: 'mobile',
      title: t('mobile', { defaultValue: 'Mobile' }),
      field: 'fleetAdminMobile',
      nowrap: true,
      cellSx: { fontVariantNumeric: 'tabular-nums' },
      render: (row) => (row.fleetAdminMobile ? phoneWithCopy(row.fleetAdminMobile) : <Box component="span" sx={{ color: c.TEXT_3 }}>{EMPTY_TEXT}</Box>),
    },
    { id: 'email', title: t('email', { defaultValue: 'Email' }), field: 'fleetAdminEmail' },
    { id: 'driverCount', title: t('driver_count', { defaultValue: 'Driver Count' }), field: 'driverCount', type: 'number', defaultSort: 'desc' },
  ];

  // The same CSV as before: the five columns, raw values.
  const exportColumns = [
    { title: t('name', { defaultValue: 'Name' }), field: 'fleetAdminName' },
    { title: t('user_type', { defaultValue: 'User Type' }), field: 'userType' },
    { title: t('mobile', { defaultValue: 'Mobile' }), field: 'fleetAdminMobile' },
    { title: t('email', { defaultValue: 'Email' }), field: 'fleetAdminEmail' },
    { title: t('driver_count', { defaultValue: 'Driver Count' }), field: 'driverCount' },
  ];

  const yes = t('yes', { defaultValue: 'Yes' });
  const no = t('no', { defaultValue: 'No' });
  const headSx = { bgcolor: c.SURFACE_ALT, color: c.INK_2, fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', py: 1 };

  const detailPanel = (row) => (
    <Box sx={{ px: { xs: 1, md: 2 } }}>
      <Typography component="h3" sx={{ fontSize: 15, fontWeight: 700, color: c.INK, mb: 1 }}>
        {t('drivers_list', { defaultValue: 'Drivers List' })}
      </Typography>
      {row.drivers && row.drivers.length > 0 ? (
        <TableContainer sx={{ border: `1px solid ${c.OUTLINE}`, borderRadius: '10px', bgcolor: c.SURFACE }}>
          <Table size="small" aria-label={`${t('drivers_list', { defaultValue: 'Drivers List' })}: ${row.fleetAdminName}`} sx={{ minWidth: 760, '& td, & th': { fontSize: 13.5, color: c.INK } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={headSx}>{t('driver_name', { defaultValue: 'Driver Name' })}</TableCell>
                <TableCell sx={headSx}>{t('mobile', { defaultValue: 'Mobile' })}</TableCell>
                <TableCell sx={headSx}>{t('email', { defaultValue: 'Email' })}</TableCell>
                <TableCell sx={headSx}>{t('has_car', { defaultValue: 'Has Car' })}</TableCell>
                <TableCell sx={headSx}>{t('license_front', { defaultValue: 'License Front' })}</TableCell>
                <TableCell sx={headSx}>{t('license_back', { defaultValue: 'Car Ownership Certificate' })}</TableCell>
                <TableCell sx={headSx}>{t('active', { defaultValue: 'Active' })}</TableCell>
                <TableCell sx={headSx}>{t('createdAt', { defaultValue: 'Date of Reg' })}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...row.drivers]
                .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
                .map((driver, index) => (
                  <TableRow key={driver.id || index}>
                    <TableCell sx={{ fontWeight: 600 }}>{driver.name}</TableCell>
                    <TableCell sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{phoneWithCopy(driver.mobile)}</TableCell>
                    <TableCell sx={{ overflowWrap: 'anywhere' }}>{driver.email}</TableCell>
                    <TableCell>
                      <YesNo value={driver.hasCar} yes={`${yes} (${driver.carCount})`} no={no} />
                    </TableCell>
                    <TableCell>
                      <YesNo value={driver.hasLicenseFront} yes={yes} no={no} />
                    </TableCell>
                    <TableCell>
                      <YesNo value={driver.hasLicenseBack} yes={yes} no={no} />
                    </TableCell>
                    <TableCell>
                      <YesNo value={driver.driverActiveStatus} yes={yes} no={no} />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{driver.createdAt ? formatAddisDateTime(driver.createdAt) : '-'}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography sx={{ fontSize: 14, color: c.INK_2 }}>{t('no_drivers', { defaultValue: 'No drivers registered' })}</Typography>
      )}
    </Box>
  );

  const ready = !!(staticusers && carlistdata.cars);
  const usersFailed = !staticusers && !usersLoading && !!(usersError && usersError.flag);
  const carsFailed = !carlistdata.cars && !carlistdata.loading && !!(carlistdata.error && carlistdata.error.flag);
  const loading = !ready && !usersFailed && !carsFailed;
  const title = calmCaps(t('fleet_drivers_report', { defaultValue: 'Scout Onboarding Report' }));

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <DataTable
        title={title}
        rows={data}
        columns={columns}
        rowKey="fleetAdminId"
        loading={loading}
        error={usersFailed || carsFailed ? { title: t('fleet_report_load_failed', { defaultValue: 'Could not load the report.' }) } : null}
        countText={({ shown, total, query, loading: busy }) =>
          busy
            ? t('loading', { defaultValue: 'Loading…' })
            : query
              ? t('table_count_shown', { shown: fmt(shown), total: fmt(total), defaultValue: '{{shown}} of {{total}}' })
              : t('table_count', { count: total, n: fmt(total), defaultValue_one: '{{n}} row', defaultValue_other: '{{n}} rows', defaultValue: '{{n}} rows' })
        }
        rowLabel={(row) => row.fleetAdminName}
        detailPanel={detailPanel}
        prefsKey="fleet_drivers_report"
        exportCsv={!!settings.AllowCriticalEditsAdmin}
        exportColumns={exportColumns}
        exportFileName="fleet_drivers_report"
        exportTitle={title}
        minWidth={760}
        emptyState={({ query }) =>
          query ? null : { icon: <GroupAddOutlinedIcon />, title: t('fleet_report_empty', { defaultValue: 'No fleet admins, agents or scouts yet' }) }
        }
      />
    </Box>
  );
}
